import { useEffect, useState } from "react";
import {
  Box,
} from "@mui/material";
import { toast } from "react-toastify";

import {
  fetchOutlet,
  Outlet,
  deleteOutlet,
} from "../../app/services/OutletService";
import { fetchStates } from "../../app/services/StatesService";
import { fetchOrganisation } from "../../app/services/OrganisationService";
import PageWrapper from "../../app/shared/common/PageWrapper";
import CreateTable from "../../app/shared/common/table/CreateTable";
import TableActions from "../../app/shared/common/table/TableAction";
import SearchBox from "../../app/shared/common/SearchBox";
import DropDown from "../../app/shared/common/DropDown";
const tableColumns = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "mobile", label: "Mobile" },
  { key: "pan_number", label: "PAN" },
  { key: "gst_number", label: "GST" },
  { key: "shipping_addressline1", label: "Address" },
  { key: "shippingpincode", label: "Pincode" },
  { key: "shippingcity", label: "City" },
  { key: "shippingState.name", label: "State" },
  { key: "Org.name", label: "Organisation" },
];
const OutletList = () => {
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [filteredOutlets, setFilteredOutlets] = useState<Outlet[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState("");
  const [stateId, setStateId] = useState<string>("");
  const [orgId, setOrgId] = useState<string>("");
  const [stateList, setStateList] = useState<{ id: number; name: string }[]>(
    []
  );
  const [organisationList, setOrganisationList] = useState<
    { id: number; name: string }[]
  >([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOutletId, setSelectedOutletId] = useState<number | null>(null);

  const loadOutlets = async () => {
    try {
      setLoading(true);
      const res = await fetchOutlet({});
      if (res.status === "success") {
        setOutlets(res.data);
        setFilteredOutlets(filterRows(res.data, searchText, stateId, orgId));
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error("Failed to load outlet.");
    } finally {
      setLoading(false);
    }
  };

  const loadStates = async () => {
    try {
      const res = await fetchStates();
      setStateList(res.data || []);
    } catch (err) {
      toast.error("Failed to load states");
    }
  };

  const loadOrganisations = async () => {
    try {
      const res = await fetchOrganisation();
      setOrganisationList(res.data || []);
    } catch (err) {
      toast.error("Failed to load organisations");
    }
  };

  const handleOpenDialog = (id: number) => {
    setSelectedOutletId(id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedOutletId(null);
    setOpenDialog(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedOutletId) return;
    try {
      await deleteOutlet(selectedOutletId);
      const updatedList = outlets.filter(
        (outlet) => outlet.id !== selectedOutletId
      );
      setOutlets(updatedList);
      setFilteredOutlets(
        filterRows(updatedList, searchText, stateId, orgId)
      );
      toast.success("Outlet deleted successfully.");
    } catch (err) {
      toast.error("Something went wrong while deleting.");
    } finally {
      handleCloseDialog();
    }
  };

  const filterRows = (
    rows: Outlet[],
    query: string,
    stateId: string,
    orgId: string
  ) => {
    return rows.filter((row) => {
      const matchesSearch = query
        ? Object.values(row).some((value) =>
            typeof value === "object"
              ? JSON.stringify(value)
                  .toLowerCase()
                  .includes(query.toLowerCase())
              : value?.toString().toLowerCase().includes(query.toLowerCase())
          )
        : true;

      const matchesState = stateId
        ? String(row.shippingState?.id ?? "") === String(stateId)
        : true;

      const matchesOrg = orgId
        ? String((row as any).org_id ?? "") === String(orgId)
        : true;

      return matchesSearch && matchesState && matchesOrg;
    });
  };

  useEffect(() => {
    loadOutlets();
    loadStates();
    loadOrganisations();
  }, []);

  useEffect(() => {
    setFilteredOutlets(filterRows(outlets, searchText, stateId, orgId));
  }, [searchText, stateId, orgId, outlets]);

  return (
    <PageWrapper
      title={"Outlets"}
      navigateTo={"/organisation/outlet/add"}
      buttonTitle={"Add Outlet"}
      actionName={"outlet"}
      open={openDialog}
      onClose={handleCloseDialog}
      onConfirm={handleConfirmDelete}
    >
      <Box display="flex" gap={2} mb={2}>
        <SearchBox value={searchText} onChange={setSearchText} />
        <DropDown
          label="Filter by State"
          value={stateId}
          onChange={setStateId}
          options={stateList}
          allLabel="All States"
        />

        <DropDown
          label="Filter by organisation"
          value={orgId}
          onChange={setOrgId}
          options={organisationList}
          allLabel="All Organisation"
        />
      </Box>
      <CreateTable
        data={filteredOutlets}
        columns={tableColumns}
        loading={loading}
        renderActions={(row) => (
          <TableActions
            navigateTo={`/organisation/outlet/update/${row.id}`}
            onClick={() => handleOpenDialog(row.id)}
          />
        )}
      />

    </PageWrapper>
  );
};

export default OutletList;
